/**
 * Caché offline de instancias DICOM (IndexedDB vía Dexie).
 * DB separada de mediciones (udvDB) y sandbox (fsDB).
 */
import Dexie from 'dexie'

const DB_NAME = 'udvDicomCache'
/** Soft cap: no rellenar más de esto sin eviction ~512 MiB */
const SOFT_MAX_BYTES = 512 * 1024 * 1024

const db = new Dexie(DB_NAME)

db.version(1).stores({
  dicom_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
})

// v2: tabla preview disociada (JPG/WebP); dicom_cache sin cambio de PK
db.version(2).stores({
  dicom_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
  preview_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
})

// v3: catálogo de estudios (metadatos para biblioteca local PWA)
db.version(3).stores({
  dicom_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
  preview_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
  study_catalog: 'studyInstanceUID, updatedAt, patientName',
})

// v4: sesiones de worklist clínico (cola plataforma + Recientes)
db.version(4).stores({
  dicom_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
  preview_cache: 'sopInstanceUID, studyInstanceUID, timestamp, status',
  study_catalog: 'studyInstanceUID, updatedAt, patientName',
  worklist_sessions: 'worklistId, receivedAt',
})

/** Exportado para studyCatalog y tests. */
export { db as dicomCacheDb }

/**
 * Flag global: true cuando el IDB upgrade quedó bloqueado por otra pestaña y
 * se agotó el timeout de 5 s. Mientras sea true, todas las funciones de caché
 * devuelven vacío en lugar de colgar indefinidamente.
 * Se resetea a false si el DB finalmente abre (ej. la otra pestaña cerró).
 */
let _dbOpenFailed = false
let _blockedTimer = null

/**
 * Callbacks registrados vía onDbFailed() que se invocan cuando el IDB es
 * marcado como no disponible (ej. write queue para limpiar la cola).
 * Evita dependencia circular: dicomCacheWriteQueue importa dicomCache,
 * pero dicomCache no puede importar dicomCacheWriteQueue.
 */
const _dbFailedCallbacks = []

/**
 * Registra un callback para cuando el IDB queda no disponible.
 * Se invoca UNA vez por evento de fallo; múltiples registros son acumulativos.
 * @param {() => void} cb
 */
export function onDbFailed(cb) {
  if (typeof cb === 'function') _dbFailedCallbacks.push(cb)
}

db.on('blocked', () => {
  console.warn(
    '[dicomCache] IDB version upgrade blocked by another tab. ' +
      'Local storage will be unavailable after 5 s.'
  )
  _blockedTimer = setTimeout(() => {
    _blockedTimer = null
    if (db.isOpen()) return
    _dbOpenFailed = true
    console.error(
      '[dicomCache] IDB open timeout – local cache disabled until page reload. ' +
        'Closing other UDV tabs and reloading will restore local storage.'
    )
    // Notificar write queue y otros suscriptores ANTES de cerrar la DB,
    // para que limpien su estado antes de que Dexie rechace ops pendientes.
    for (let i = 0; i < _dbFailedCallbacks.length; i++) {
      try {
        _dbFailedCallbacks[i]()
      } catch (e) {
        /* ignore */
      }
    }
    // Cerrar la DB fuerza que las operaciones Dexie pendientes rechacen con
    // DatabaseClosedError, desbloqueando todos los awaits colgados.
    try {
      db.close()
    } catch (e) {
      /* ignore */
    }
  }, 5000)
})

db.on('ready', () => {
  _dbOpenFailed = false
  if (_blockedTimer) {
    clearTimeout(_blockedTimer)
    _blockedTimer = null
  }
})

export function openDicomCacheDb() {
  return db.open()
}

/** true si la DB está disponible (no está en estado de timeout por bloqueo). */
export function isDbAvailable() {
  return !_dbOpenFailed
}
export const CACHE_KIND = {
  DICOM: 'dicom',
  PREVIEW: 'preview',
}

export const DICOM_OFFLINE_CACHE_ENABLED = true

/**
 * @typedef {'complete'|'partial'} CacheStatus
 * @typedef {'dicom'|'preview'} CacheKind
 * @typedef {{
 *   sopInstanceUID: string,
 *   studyInstanceUID: string,
 *   blob: Blob|ArrayBuffer,
 *   timestamp: number,
 *   byteLength: number,
 *   expectedByteLength?: number|null,
 *   status: CacheStatus,
 *   contentType?: string,
 *   kind?: CacheKind,
 * }} DicomCacheRow
 */

function isQuotaError(err) {
  if (!err) return false
  const name = err.name || ''
  const msg = String(err.message || err || '')
  if (
    name === 'QuotaExceededError' ||
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    /quota/i.test(msg)
  ) {
    return true
  }
  // Dexie envuelve QuotaExceeded como AbortError con message "QuotaExceededError"
  if (name === 'AbortError' && /quota/i.test(msg)) return true
  if (err.inner && isQuotaError(err.inner)) return true
  if (err._e && isQuotaError(err._e)) return true
  return false
}

/** Exportado para loaders / upgrade on-demand. */
export function isQuotaLikeError(err) {
  return isQuotaError(err)
}

/**
 * @param {Blob|ArrayBuffer|Uint8Array|null|undefined} data
 * @param {string} [contentType]
 * @returns {Blob|null}
 */
export function toBlob(data, contentType = 'application/dicom') {
  if (data == null) return null
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data
  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: contentType })
  }
  if (ArrayBuffer.isView(data)) {
    const view = data
    const copy = view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength)
    return new Blob([copy], { type: contentType })
  }
  return null
}

/**
 * @param {Blob|ArrayBuffer|null|undefined} data
 * @returns {Promise<ArrayBuffer|null>}
 */
export async function toArrayBuffer(data) {
  if (data == null) return null
  if (data instanceof ArrayBuffer) return data
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return data.arrayBuffer()
  }
  if (ArrayBuffer.isView(data)) {
    const view = data
    return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength)
  }
  return null
}

/**
 * @param {string} sopInstanceUID
 * @returns {Promise<DicomCacheRow|undefined>}
 */
export async function getCachedInstance(sopInstanceUID) {
  return getCachedAsset(sopInstanceUID, CACHE_KIND.DICOM)
}

/**
 * @param {string} sopInstanceUID
 * @param {CacheKind} [kind]
 */
export async function getCachedAsset(sopInstanceUID, kind = CACHE_KIND.DICOM) {
  if (!sopInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return undefined
  try {
    if (kind === CACHE_KIND.PREVIEW) {
      return await db.preview_cache.get(String(sopInstanceUID))
    }
    return await db.dicom_cache.get(String(sopInstanceUID))
  } catch (e) {
    console.warn('[dicomCache] get', e)
    return undefined
  }
}

/**
 * @param {string} sopInstanceUID
 */
export async function getCachedPreview(sopInstanceUID) {
  return getCachedAsset(sopInstanceUID, CACHE_KIND.PREVIEW)
}

/**
 * @param {object} params
 * @param {string} params.sopInstanceUID
 * @param {string} params.studyInstanceUID
 * @param {Blob|ArrayBuffer} params.blob
 * @param {number} [params.expectedByteLength]
 * @param {CacheStatus} [params.status]
 * @param {string} [params.contentType]
 * @param {CacheKind} [params.kind]
 */
export async function putCachedInstance({
  sopInstanceUID,
  studyInstanceUID,
  blob,
  expectedByteLength,
  status = 'complete',
  contentType = 'application/dicom',
  kind = CACHE_KIND.DICOM,
}) {
  if (!DICOM_OFFLINE_CACHE_ENABLED || !sopInstanceUID || _dbOpenFailed) return false
  const ct = contentType || (kind === CACHE_KIND.PREVIEW ? 'image/jpeg' : 'application/dicom')
  const b = toBlob(blob, ct)
  if (!b) return false
  const byteLength = typeof b.size === 'number' ? b.size : 0
  const row = {
    sopInstanceUID: String(sopInstanceUID),
    studyInstanceUID: String(studyInstanceUID || ''),
    blob: b,
    timestamp: Date.now(),
    byteLength,
    expectedByteLength:
      expectedByteLength != null && Number.isFinite(expectedByteLength)
        ? expectedByteLength
        : status === 'complete'
          ? byteLength
          : null,
    status,
    contentType: ct,
    kind,
  }

  const table = kind === CACHE_KIND.PREVIEW ? db.preview_cache : db.dicom_cache

  const tryPut = async () => {
    await table.put(row)
    return true
  }

  try {
    await tryPut()
    return true
  } catch (e) {
    if (!isQuotaError(e)) {
      console.warn('[dicomCache] put', e)
      return false
    }
    try {
      await evictLruUntil(Math.max(byteLength * 2, 8 * 1024 * 1024))
      await tryPut()
      return true
    } catch (e2) {
      // AbortError/QuotaExceeded es esperado bajo presión — no spamear stack
      if (!isQuotaError(e2)) {
        console.warn('[dicomCache] put after eviction failed', e2)
      }
      return false
    }
  }
}

/**
 * @param {object} params
 */
export async function putCachedPreview(params) {
  return putCachedInstance({
    ...params,
    kind: CACHE_KIND.PREVIEW,
    contentType: (params && params.contentType) || 'image/jpeg',
    status: 'complete',
  })
}

/**
 * Parcial o reemplazo por reanudar Range.
 * @param {object} params
 */
export async function putPartialInstance(params) {
  return putCachedInstance({
    ...params,
    status: 'partial',
  })
}

/**
 * Concatena buffer parcial existente + range body.
 * @param {ArrayBuffer} existing
 * @param {ArrayBuffer} next
 * @returns {ArrayBuffer}
 */
export function concatArrayBuffers(existing, next) {
  const a = existing instanceof ArrayBuffer ? new Uint8Array(existing) : new Uint8Array(0)
  const b = next instanceof ArrayBuffer ? new Uint8Array(next) : new Uint8Array(0)
  const out = new Uint8Array(a.length + b.length)
  out.set(a, 0)
  out.set(b, a.length)
  return out.buffer
}

/**
 * Une listas de StudyInstanceUID sin duplicados.
 * @param {...(string[]|null|undefined)[]} lists
 * @returns {string[]}
 */
export function mergeStudyUidLists(...lists) {
  const out = new Set()
  for (let i = 0; i < lists.length; i++) {
    const list = lists[i]
    if (!Array.isArray(list)) continue
    for (let j = 0; j < list.length; j++) {
      const uid = list[j]
      if (uid) out.add(String(uid))
    }
  }
  return [...out]
}

/**
 * @param {string} studyInstanceUID
 * @returns {Promise<string[]>} UIDs de estudio presentes en dicom_cache
 */
export async function listCachedStudyUIDs() {
  if (!DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return []
  try {
    const keys = await db.dicom_cache.orderBy('studyInstanceUID').uniqueKeys()
    return (keys || []).map(String).filter(Boolean)
  } catch (e) {
    console.warn('[dicomCache] listCachedStudyUIDs', e)
    return []
  }
}

/**
 * @returns {Promise<string[]>} UIDs de estudio presentes en preview_cache
 */
export async function listCachedPreviewStudyUIDs() {
  if (!DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return []
  try {
    const keys = await db.preview_cache.orderBy('studyInstanceUID').uniqueKeys()
    return (keys || []).map(String).filter(Boolean)
  } catch (e) {
    console.warn('[dicomCache] listCachedPreviewStudyUIDs', e)
    return []
  }
}

/**
 * Unión de UIDs en dicom_cache y preview_cache.
 * @returns {Promise<string[]>}
 */
export async function listAllCachedStudyUIDs() {
  const [dicom, preview] = await Promise.all([
    listCachedStudyUIDs(),
    listCachedPreviewStudyUIDs(),
  ])
  return mergeStudyUidLists(dicom, preview)
}

/**
 * @param {string} studyInstanceUID
 * @returns {Promise<string[]>}
 */
export async function listCachedSopsForStudy(studyInstanceUID) {
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return []
  try {
    const rows = await db.dicom_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .primaryKeys()
    return rows.map(String)
  } catch (e) {
    console.warn('[dicomCache] listCachedSopsForStudy', e)
    return []
  }
}

/**
 * Solo SOP con status complete.
 * @param {string} studyInstanceUID
 * @returns {Promise<Set<string>>}
 */
export async function listCompleteSopsForStudy(studyInstanceUID) {
  const set = new Set()
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return set
  try {
    await db.dicom_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .each((row) => {
        if (row && row.status === 'complete' && row.sopInstanceUID) {
          set.add(String(row.sopInstanceUID))
        }
      })
  } catch (e) {
    console.warn('[dicomCache] listCompleteSopsForStudy', e)
  }
  return set
}

/**
 * @param {string} studyInstanceUID
 * @returns {Promise<string[]>}
 */
export async function listCachedPreviewSopsForStudy(studyInstanceUID) {
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED) return []
  try {
    const rows = await db.preview_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .primaryKeys()
    return rows.map(String)
  } catch (e) {
    console.warn('[dicomCache] listCachedPreviewSopsForStudy', e)
    return []
  }
}

/**
 * Solo SOP preview con status complete.
 * @param {string} studyInstanceUID
 * @returns {Promise<Set<string>>}
 */
export async function listCompletePreviewSopsForStudy(studyInstanceUID) {
  const set = new Set()
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED) return set
  try {
    await db.preview_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .each((row) => {
        if (row && row.status === 'complete' && row.sopInstanceUID) {
          set.add(String(row.sopInstanceUID))
        }
      })
  } catch (e) {
    console.warn('[dicomCache] listCompletePreviewSopsForStudy', e)
  }
  return set
}

/**
 * SOPs preview complete ordenados por timestamp de descarga.
 * @param {string} studyInstanceUID
 * @returns {Promise<string[]>}
 */
export async function listCompletePreviewSopsOrderedForStudy(studyInstanceUID) {
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED) return []
  try {
    const rows = await db.preview_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .toArray()
    return (rows || [])
      .filter((row) => row && row.status === 'complete' && row.sopInstanceUID)
      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
      .map((row) => String(row.sopInstanceUID))
  } catch (e) {
    console.warn('[dicomCache] listCompletePreviewSopsOrderedForStudy', e)
    return []
  }
}

/**
 * @param {string} studyInstanceUID
 */
export async function limpiarCacheEstudio(studyInstanceUID) {
  if (!studyInstanceUID) return 0
  try {
    const a = await db.dicom_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .delete()
    const b = await db.preview_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .delete()
    return (a || 0) + (b || 0)
  } catch (e) {
    console.warn('[dicomCache] limpiarCacheEstudio', e)
    return 0
  }
}

export async function vaciarCacheTotal() {
  try {
    await db.dicom_cache.clear()
    await db.preview_cache.clear()
    return true
  } catch (e) {
    console.warn('[dicomCache] vaciarCacheTotal', e)
    return false
  }
}

/**
 * @param {string} studyInstanceUID
 * @param {string[]} [expectedSopList]
 */
export async function getStudyCacheStats(studyInstanceUID, expectedSopList) {
  const empty = {
    cachedComplete: 0,
    totalKnown: Array.isArray(expectedSopList) ? expectedSopList.length : 0,
    bytes: 0,
    studyOffline: false,
  }
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return empty

  let cachedComplete = 0
  let bytes = 0
  try {
    await db.dicom_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .each((row) => {
        if (!row) return
        if (row.status === 'complete') {
          cachedComplete += 1
          bytes += row.byteLength || 0
        }
      })
  } catch (e) {
    console.warn('[dicomCache] getStudyCacheStats', e)
    return empty
  }

  const totalKnown = Array.isArray(expectedSopList)
    ? expectedSopList.length
    : cachedComplete
  return {
    cachedComplete,
    totalKnown,
    bytes,
    studyOffline: totalKnown > 0 && cachedComplete >= totalKnown,
  }
}

/**
 * Stats combinadas dicom_cache + preview_cache (SOP únicos para conteo).
 * @param {string} studyInstanceUID
 * @param {string[]} [expectedSopList]
 */
export async function getCombinedStudyCacheStats(studyInstanceUID, expectedSopList) {
  const dicomStats = await getStudyCacheStats(studyInstanceUID, expectedSopList)
  const empty = {
    ...dicomStats,
    dicomComplete: dicomStats.cachedComplete,
    previewComplete: 0,
    cachedComplete: dicomStats.cachedComplete,
  }
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED || _dbOpenFailed) return empty

  const completeSops = new Set()
  let previewComplete = 0
  let previewBytes = 0
  try {
    await db.dicom_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .each((row) => {
        if (row && row.status === 'complete' && row.sopInstanceUID) {
          completeSops.add(String(row.sopInstanceUID))
        }
      })
    await db.preview_cache
      .where('studyInstanceUID')
      .equals(String(studyInstanceUID))
      .each((row) => {
        if (!row || row.status !== 'complete' || !row.sopInstanceUID) return
        const sop = String(row.sopInstanceUID)
        if (!completeSops.has(sop)) {
          previewComplete += 1
        }
        completeSops.add(sop)
        previewBytes += row.byteLength || 0
      })
  } catch (e) {
    console.warn('[dicomCache] getCombinedStudyCacheStats', e)
    return empty
  }

  const cachedComplete = completeSops.size
  const totalKnown = Array.isArray(expectedSopList)
    ? expectedSopList.length
    : cachedComplete
  return {
    cachedComplete,
    dicomComplete: dicomStats.cachedComplete,
    previewComplete,
    totalKnown,
    bytes: (dicomStats.bytes || 0) + previewBytes,
    studyOffline: totalKnown > 0 && cachedComplete >= totalKnown,
  }
}

/**
 * Elimina entradas complete más antiguas hasta liberar ~targetBytes.
 * @param {number} targetBytes
 */
export async function evictLruUntil(targetBytes) {
  const need = Math.max(0, targetBytes || 0)
  if (need <= 0) return 0

  let freed = 0
  try {
    // Preferir borrar previews antiguas primero (menor valor clínico)
    const previews = await db.preview_cache.orderBy('timestamp').toArray()
    for (const row of previews) {
      if (freed >= need) break
      if (!row || !row.sopInstanceUID) continue
      const sz = row.byteLength || 0
      await db.preview_cache.delete(row.sopInstanceUID)
      freed += sz
    }
    if (freed < need) {
      const rows = await db.dicom_cache.orderBy('timestamp').toArray()
      for (const row of rows) {
        if (freed >= need) break
        if (!row || !row.sopInstanceUID) continue
        const sz = row.byteLength || 0
        await db.dicom_cache.delete(row.sopInstanceUID)
        freed += sz
      }
    }
  } catch (e) {
    console.warn('[dicomCache] evictLruUntil', e)
  }
  return freed
}

/**
 * Estimación simple del tamaño total en caché.
 */
export async function getTotalCacheBytes() {
  let bytes = 0
  try {
    await db.dicom_cache.each((row) => {
      if (row) bytes += row.byteLength || 0
    })
    await db.preview_cache.each((row) => {
      if (row) bytes += row.byteLength || 0
    })
  } catch (e) {
    /* ignore */
  }
  return bytes
}

/**
 * Si estamos por encima del soft max, liberar espacio.
 */
export async function ensureSoftQuota() {
  try {
    const total = await getTotalCacheBytes()
    if (total > SOFT_MAX_BYTES) {
      await evictLruUntil(total - SOFT_MAX_BYTES + 16 * 1024 * 1024)
    }
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      const est = await navigator.storage.estimate()
      if (est && est.quota && est.usage != null) {
        const free = est.quota - est.usage
        if (free < 32 * 1024 * 1024) {
          await evictLruUntil(64 * 1024 * 1024)
        }
      }
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * Recuperación agresiva ante QuotaExceeded (medición on-demand).
 * Libera previews + LRU DICOM; no borrado total salvo free muy bajo.
 * @returns {Promise<number>} bytes liberados (aprox)
 */
export async function recoverFromQuotaPressure() {
  let freed = 0
  try {
    freed += await evictLruUntil(128 * 1024 * 1024)
  } catch (e) {
    /* ignore */
  }
  try {
    await ensureSoftQuota()
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      const est = await navigator.storage.estimate()
      if (est && est.quota && est.usage != null) {
        const free = est.quota - est.usage
        if (free < 16 * 1024 * 1024) {
          freed += await evictLruUntil(256 * 1024 * 1024)
        }
      }
    }
  } catch (e) {
    /* ignore */
  }
  return freed
}

/**
 * Borrar clave temporal (hash de url) tras promover a SOP real.
 * @param {string} key
 */
export async function deleteCachedKey(key) {
  if (!key) return
  try {
    await db.dicom_cache.delete(String(key))
  } catch (e) {
    /* ignore */
  }
}

/**
 * Borra entradas preview + dicom para una lista de SOPs.
 * @param {string[]} sopList
 * @returns {Promise<number>} filas borradas
 */
export async function deleteCachedSops(sopList) {
  if (!DICOM_OFFLINE_CACHE_ENABLED || !Array.isArray(sopList) || !sopList.length) {
    return 0
  }
  let n = 0
  for (const raw of sopList) {
    const sop = raw != null ? String(raw).trim() : ''
    if (!sop) continue
    try {
      await db.dicom_cache.delete(sop)
      n += 1
    } catch (e) {
      /* ignore */
    }
    try {
      await db.preview_cache.delete(sop)
      n += 1
    } catch (e) {
      /* ignore */
    }
  }
  return n
}

export default db
